import React, {createRef, ReactElement, ReactNode} from 'react';
import {Link} from 'gatsby';
import {cx} from '@emotion/css';

import {CONSENT_GROUPS_QUERY_PARAM, LANGUAGE_LOCALE} from '@fo/shared-types/constants';
import {enforceTrailingSlashToUrlOrRoute} from '@fo/shared-helpers/enforceTrailingSlashToUrlOrRoute';
import {useIdentityContext} from '@fo/shared-providers/IdentityContext';

import styles from './DynamicLink.styles';

interface Props {
  children: ReactNode;
  href: string;
  onClick?: (ev: React.MouseEvent<HTMLAnchorElement>) => void;
  id?: string;
  className?: string;
  activeClassName?: string;
  testId?: string;
  ariaLabel?: string;
  blank?: boolean;
  title?: string;
  isDisabled?: boolean;
  forceAnchorLink?: boolean;
  download?: boolean;
}

// To be used only when we are unsure of the link being rendered (CMS content)
// always favour using <Link> directly
function DynamicLink({
  children,
  href,
  onClick,
  id,
  className,
  activeClassName,
  ariaLabel,
  testId,
  blank,
  title,
  download,
  isDisabled = false,
  forceAnchorLink = blank,
}: Readonly<Props>): ReactElement {
  const {isUserAdvisor, isUserBRSAdvisor} = useIdentityContext() || {};
  const internalURLPattern = /^(https?:)?(\/\/)?([\w-]*\.)*fundingoptions\.com(\/)?/i;
  const aRef = createRef<HTMLAnchorElement>();

  if (!href) {
    // this only happens on build occasionally for contentful previews
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  // if href includes '/get-started' and the flag is set to allow redirecting to Tide
  // user is also not an advisor or BRS advisor
  const isTideFlow: boolean = /\/get-started/.test(href) && !isUserAdvisor && !isUserBRSAdvisor;

  // check if link is an internal link
  const isInternalLink = !forceAnchorLink && (href.startsWith('/') || href.match(internalURLPattern));
  // make sure all urls
  const processedHref = enforceTrailingSlashToUrlOrRoute(href);
  const url = isInternalLink ? processedHref.replace(internalURLPattern, '/') : processedHref;

  const addConsentGroupsToUrlQueryParams = (originalUrl: string) => {
    if (/^(http)/.exec(originalUrl)) {
      const urlObject = new URL(originalUrl);
      if (urlObject.hostname.includes('.tide.co') && aRef.current) {
        const searchParams = new URLSearchParams(urlObject.searchParams);
        searchParams.append(CONSENT_GROUPS_QUERY_PARAM, window.OnetrustActiveGroups);
        aRef.current.href = `${urlObject.origin}${urlObject.pathname}?${searchParams.toString()}`;
      }
    }
  };

  // handle fundingoptions urls
  if (isInternalLink) {
    return (
      <Link
        download={download}
        activeClassName={cx(activeClassName)}
        aria-label={ariaLabel}
        id={id}
        className={cx(styles.link(isDisabled), className)}
        data-testid={testId}
        hrefLang={LANGUAGE_LOCALE}
        onClick={onClick}
        target={blank ? '_blank' : '_self'}
        title={title}
        to={url}
        state={isTideFlow ? {isTideFlow} : undefined}
      >
        {children}
      </Link>
    );
  }
  // external link
  return (
    <a
      aria-label={ariaLabel}
      download={download}
      id={id}
      className={cx(styles.link(isDisabled), className)}
      data-testid={testId}
      href={url}
      hrefLang={LANGUAGE_LOCALE}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
        // Click event happens before executing the href
        // so we keep the href for SEO purposes
        // and onClick modify it on-the-fly to include the consent groups
        addConsentGroupsToUrlQueryParams(url);
        if (onClick) {
          onClick(event);
        }
      }}
      rel="nofollow noopener noreferrer external"
      target={blank ? '_blank' : '_self'}
      title={title}
      ref={aRef}
    >
      {children}
    </a>
  );
}

export default DynamicLink;
